import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

const PrivacyPage = () => {
  return (
    <div className="min-h-screen bg-[#F5F5F5] flex flex-col">
      <header className="bg-white shadow-sm">
        <div className="max-w-3xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center text-[#424E59] hover:text-[#1D2D44] text-sm">
            <ArrowLeft className="w-4 h-4 mr-1" />
            Back
          </Link>
          <span className="font-serif text-lg text-[#1D2D44]">Privacy Policy</span>
          <div className="w-16"></div>
        </div>
      </header>

      <main className="flex-1 max-w-3xl mx-auto px-4 py-10">
        <h1 className="font-serif text-3xl text-[#1D2D44] mb-1" data-testid="privacy-title">Our Love <span style={{color:"#C9A84C"}}>&#9829;</span> Connection</h1>
        <h2 className="font-serif text-xl text-[#1D2D44] mb-1">Privacy Policy</h2>
        <p className="text-[#9AA5A6] text-sm mb-4">Last updated: September 2026</p>
        <p className="text-[#3A4A5A] text-sm leading-relaxed mb-10">Our Love <span style={{color:"#C9A84C"}}>&#9829;</span> Connection is built as a calm, intentional space for personal reflection and clarity around relationships. Your privacy matters, and we take care to handle your information respectfully and transparently.</p>

        <div className="space-y-8 text-[#3A4A5A] text-sm leading-relaxed" data-testid="privacy-content">

          <section>
            <h3 className="font-serif text-lg text-[#1D2D44] mb-2">Information We Collect</h3>
            <p className="mb-3">We collect only what is necessary to provide the experience.</p>
            <p className="mb-2">This may include:</p>
            <ul className="space-y-1.5 mb-4">
              <li className="flex items-start"><span className="w-1.5 h-1.5 bg-[#B89B5E] rounded-full mt-1.5 mr-3 flex-shrink-0"></span>Basic account information (such as email address)</li>
              <li className="flex items-start"><span className="w-1.5 h-1.5 bg-[#B89B5E] rounded-full mt-1.5 mr-3 flex-shrink-0"></span>Your responses within the Relationship Map and other reflections</li>
              <li className="flex items-start"><span className="w-1.5 h-1.5 bg-[#B89B5E] rounded-full mt-1.5 mr-3 flex-shrink-0"></span>Profile photos you choose to upload, stored privately and accessible only to you and platform administrators.</li>
              <li className="flex items-start"><span className="w-1.5 h-1.5 bg-[#B89B5E] rounded-full mt-1.5 mr-3 flex-shrink-0"></span>Profile information you choose to provide, including name, age, city, and relationship preferences.</li>
              <li className="flex items-start"><span className="w-1.5 h-1.5 bg-[#B89B5E] rounded-full mt-1.5 mr-3 flex-shrink-0"></span>Basic technical information (such as device type or browser, used only to maintain and improve the platform)</li>
            </ul>
            <p className="mt-4">We use Google Analytics on our landing page to better understand website traffic and usage, such as pages visited and general device or browser information. This information is aggregated where possible and is not used by us to identify individual visitors.</p>
            <p className="mb-2">We do not collect:</p>
            <ul className="space-y-1.5">
              <li className="flex items-start"><span className="w-1.5 h-1.5 bg-[#424E59] rounded-full mt-1.5 mr-3 flex-shrink-0"></span>Biometric data (such as facial scans or voice data)</li>
              <li className="flex items-start"><span className="w-1.5 h-1.5 bg-[#424E59] rounded-full mt-1.5 mr-3 flex-shrink-0"></span>Background check information</li>
              <li className="flex items-start"><span className="w-1.5 h-1.5 bg-[#424E59] rounded-full mt-1.5 mr-3 flex-shrink-0"></span>Location tracking data beyond what you choose to enter</li>
              <li className="flex items-start"><span className="w-1.5 h-1.5 bg-[#424E59] rounded-full mt-1.5 mr-3 flex-shrink-0"></span>Information from your contacts or social accounts</li>
            </ul>
          </section>

          <section>
            <h3 className="font-serif text-lg text-[#1D2D44] mb-2">How Your Information Is Used</h3>
            <p className="mb-2">Your information is used to:</p>
            <ul className="space-y-1.5 mb-4">
              <li className="flex items-start"><span className="w-1.5 h-1.5 bg-[#B89B5E] rounded-full mt-1.5 mr-3 flex-shrink-0"></span>Provide access to your account</li>
              <li className="flex items-start"><span className="w-1.5 h-1.5 bg-[#B89B5E] rounded-full mt-1.5 mr-3 flex-shrink-0"></span>Save and display your reflections</li>
              <li className="flex items-start"><span className="w-1.5 h-1.5 bg-[#B89B5E] rounded-full mt-1.5 mr-3 flex-shrink-0"></span>Maintain the functionality and security of the platform</li>
              <li className="flex items-start"><span className="w-1.5 h-1.5 bg-[#B89B5E] rounded-full mt-1.5 mr-3 flex-shrink-0"></span>Improve clarity, usability, and performance over time</li>
            </ul>
            <p className="mb-2">Your reflections are private by default. They are not shared publicly and are not visible to other users.</p>
            <p className="font-medium text-[#1D2D44]">We do not sell your personal data.</p>
          </section>

          <section>
            <h3 className="font-serif text-lg text-[#1D2D44] mb-2">Data Sharing</h3>
            <p className="mb-2">We do not share your personal information with third parties except:</p>
            <ul className="space-y-1.5 mb-3">
              <li className="flex items-start"><span className="w-1.5 h-1.5 bg-[#B89B5E] rounded-full mt-1.5 mr-3 flex-shrink-0"></span>When required to operate the platform (for example, secure hosting or payment processing services)</li>
              <li className="flex items-start"><span className="w-1.5 h-1.5 bg-[#B89B5E] rounded-full mt-1.5 mr-3 flex-shrink-0"></span>When required by law</li>
            </ul>
            <p className="mb-3">Any service providers we use are expected to handle data responsibly and only for their intended purpose.</p>
            <p className="mb-2">Payment processing is handled by PayPal. We do not store or have access to your payment card information.</p>
            <p>Email delivery services are used to send account verification and password reset emails.</p>
          </section>

          <section>
            <h3 className="font-serif text-lg text-[#1D2D44] mb-2">Data Retention</h3>
            <p className="mb-2">We retain your information only as long as your account remains active or as needed to provide the service.</p>
            <p>You may request deletion of your account and associated data at any time.</p>
          </section>

          <section>
            <h3 className="font-serif text-lg text-[#1D2D44] mb-2">Your Choices and Rights</h3>
            <p className="mb-2">You may:</p>
            <ul className="space-y-1.5 mb-3">
              <li className="flex items-start"><span className="w-1.5 h-1.5 bg-[#B89B5E] rounded-full mt-1.5 mr-3 flex-shrink-0"></span>Access your personal information</li>
              <li className="flex items-start"><span className="w-1.5 h-1.5 bg-[#B89B5E] rounded-full mt-1.5 mr-3 flex-shrink-0"></span>Request correction of inaccurate information</li>
              <li className="flex items-start"><span className="w-1.5 h-1.5 bg-[#B89B5E] rounded-full mt-1.5 mr-3 flex-shrink-0"></span>Request deletion of your account and reflections</li>
            </ul>
            <p>To make a request, contact us at: <a href="mailto:hello@ourloveconnection.co" className="text-[#B89B5E] underline">hello@ourloveconnection.co</a></p>
          </section>

          <section>
            <h3 className="font-serif text-lg text-[#1D2D44] mb-2">Security</h3>
            <p>We take reasonable steps to protect your information. No system can be guaranteed 100% secure, but we aim to handle your data with care and responsibility.</p>
          </section>

          <section className="p-6 bg-white rounded-2xl shadow-sm border border-[#B89B5E]/10" data-testid="non-therapy-disclaimer">
            <h3 className="font-serif text-lg text-[#1D2D44] mb-3">Non-Therapy Disclaimer</h3>
            <div className="space-y-3">
              <p>Our Love <span style={{color:"#C9A84C"}}>&#9829;</span> Connection is a self-guided reflection platform designed to support thoughtful consideration of values, readiness, and alignment in relationships.</p>
              <p>It is not therapy, counseling, coaching, medical care, or mental health treatment. The platform does not provide diagnoses, treatment, or professional advice.</p>
              <p>If you are experiencing emotional distress or need professional support, we encourage you to seek help from a qualified mental health professional.</p>
              <p>Users are responsible for their own decisions, actions, and relationships. Our Love <span style={{color:"#C9A84C"}}>&#9829;</span> Connection provides reflective prompts and informational content but does not direct or control user behavior, outcomes, or relationship choices.</p>
            </div>
          </section>

          <section>
            <h3 className="font-serif text-lg text-[#1D2D44] mb-2">Governing Law</h3>
            <p>This Privacy Policy is governed by the laws of the State of Missouri, United States.</p>
          </section>

          <section>
            <h3 className="font-serif text-lg text-[#1D2D44] mb-2">Additional Rights</h3>
            <p>If you are a California resident or located outside the United States, you may have additional rights regarding your personal data. Contact us at <a href="mailto:hello@ourloveconnection.co" className="text-[#B89B5E] underline">hello@ourloveconnection.co</a> for more information.</p>
          </section>

          <section>
            <h3 className="font-serif text-lg text-[#1D2D44] mb-2">Changes to This Policy</h3>
            <p>If this Privacy Policy changes, we will update the &ldquo;Last updated&rdquo; date and make the revised version available within the app.</p>
          </section>

          <section>
            <h3 className="font-serif text-lg text-[#1D2D44] mb-2">Contact</h3>
            <p>If you have questions about privacy or data use, contact us at: <a href="mailto:hello@ourloveconnection.co" className="text-[#B89B5E] underline">hello@ourloveconnection.co</a></p>
          </section>

        </div>

        <div className="mt-12 pt-8 border-t border-[#B89B5E]/20">
          <p className="text-sm text-[#9AA5A6]">
            See also: <Link to="/terms" className="underline hover:text-[#1D2D44]">Terms of Service</Link>
          </p>
        </div>
      </main>
    </div>
  );
};

export default PrivacyPage;
